import { IRefund, Terminal } from '@stripe/terminal-js'
import { fetchApi } from '@vend/utilities'
import VendNumber from 'vend-number'
import { ApiTransaction } from '@vend/types'
import {
  PAYMENT_STATUS,
  TERMINAL_EVENT,
  TerminalEventMonitoring,
} from '@lightspeed-payments/payments-terminal-sdk'
import { TFunction } from '@transifex/react'
import { TimerStarterFunction } from '../hooks/timeTracking'
import { isErrorResponse } from './errorChecking'
import {
  CreateRefundOptions,
  CreateRefundResponse,
  LSPAYRefundOptions,
  RefundResponse,
  SwiperError,
  SwiperErrorResponse,
  GetRefundOptions,
  UnrefRefundReason,
  ReprocessRefundOptions,
} from './lspay.types'
import {
  LSPAY_ERROR,
  LSPAY_PRODUCT_VERSION,
  LSPAY_STEPS,
  SWIPER_ERROR_CODES,
  TRANSACTION_TYPE,
} from './lspayConstants'
import { trackTerminalCompleteWithError } from './terminalTracking'

export type RefundMethod =
  | { type: 'interac'; chargeId: string }
  | { type: 'unreferenced' }

export const refund = async ({
  registerID,
  registerName,
  currency,
  retailerPaymentTypeID,
  registerSalePaymentID,
  outletId,
  amount,
  trackTiming,
  originalRegisterSalePaymentId,
  refundMethod,
  unreferencedPaymentMethodID,
}: LSPAYRefundOptions): Promise<ApiTransaction> => {
  const stopCaptureTimer = trackTiming(LSPAY_STEPS.CAPTURE)
  const response = await fetchApi(
    `/api/2.0/registers/${registerID}/transactions/${retailerPaymentTypeID}/refund/${registerSalePaymentID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: amount.toNumber(),
        currency,
        original_register_sale_payment_id: originalRegisterSalePaymentId,
        outlet_id: outletId,
        lspay_refund_method: refundMethod?.type,
        lspay_unreferenced_payment_method_id: unreferencedPaymentMethodID,
        register_name: registerName,
        product_version: LSPAY_PRODUCT_VERSION,
      }),
    }
  )
  stopCaptureTimer()

  if (response.status < 200 || response.status > 299) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }

  return response.json()
}

export const refundWithTerminal = async (
  chargeId: string,
  amount: VendNumber,
  currency: string,
  terminal: Terminal,
  terminalMonitoringInstance: TerminalEventMonitoring,
  trackTiming: TimerStarterFunction,
  isRetry: boolean
): Promise<IRefund> => {
  const stripeAmount = amount.mul(100).abs().toNumber()
  const stripeCurrency = currency.toLowerCase()

  if (isRetry) {
    // Not updating to the use the new property due to neglibile volume of usage
    terminalMonitoringInstance.sendEvent(TERMINAL_EVENT.START, {
      transactionType: TRANSACTION_TYPE.REFUND,
    })
  }
  const stopCollectTimer = trackTiming(LSPAY_STEPS.COLLECT)
  try {
    const collectRefundMethodResult = await terminal.collectRefundPaymentMethod(
      chargeId,
      stripeAmount,
      stripeCurrency
    )
    stopCollectTimer()

    if (isErrorResponse(collectRefundMethodResult)) {
      throw collectRefundMethodResult.error
    }

    const stopProcessTimer = trackTiming(LSPAY_STEPS.PROCESS)
    const refundResult = await terminal.processRefund()
    stopProcessTimer()

    if (isErrorResponse(refundResult)) {
      throw refundResult.error
    }

    terminalMonitoringInstance.sendEvent(TERMINAL_EVENT.COMPLETE, {
      payment: {
        status: PAYMENT_STATUS.APPROVED,
      },
      transactionType: TRANSACTION_TYPE.REFUND,
    })
    return refundResult.refund
  } catch (error) {
    trackTerminalCompleteWithError(terminalMonitoringInstance, error, true)
    throw error
  }
}

// Returns payment method Id
export const readReusableCard = async (
  terminal: Terminal,
  terminalMonitoringInstance: TerminalEventMonitoring,
  isRetry: boolean
): Promise<string> => {
  if (isRetry) {
    // Not updating to the use the new property due to neglibile volume of usage
    terminalMonitoringInstance.sendEvent(TERMINAL_EVENT.START, {
      transactionType: TRANSACTION_TYPE.REFUND,
    })
  }
  try {
    const readReusableCardResponse = await terminal.readReusableCard()
    if (isErrorResponse(readReusableCardResponse)) {
      throw readReusableCardResponse.error
    }

    const paymentMethodId = readReusableCardResponse.payment_method.id

    if (!paymentMethodId) {
      throw new Error(
        'Payment method id received read reusable card was empty.'
      )
    }

    terminalMonitoringInstance.sendEvent(TERMINAL_EVENT.COMPLETE, {
      payment: {
        status: PAYMENT_STATUS.APPROVED,
      },
      transactionType: TRANSACTION_TYPE.REFUND,
    })
    return paymentMethodId
  } catch (error) {
    trackTerminalCompleteWithError(terminalMonitoringInstance, error, true)
    throw error
  }
}

export const createRefund = async ({
  amount,
  currency,
  registerId,
  retailerPaymentTypeId,
  registerSalePaymentId,
  metadata,
  consentCollected,
  originalRegisterSalePaymentIds,
}: CreateRefundOptions): Promise<CreateRefundResponse> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/refunds`,
    {
      method: 'POST',
      body: JSON.stringify({
        amount,
        currency,
        register_id: registerId,
        retailer_payment_type_id: retailerPaymentTypeId,
        register_sale_payment_id: registerSalePaymentId,
        consent_collected: consentCollected,
        original_register_sale_payment_ids: originalRegisterSalePaymentIds,
        metadata: {
          ...metadata,
          product_version: LSPAY_PRODUCT_VERSION,
        },
      }),
    }
  )

  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()
    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }
  return response.json()
}
// Get the refund status
export const getRefund = async ({
  registerId,
  setupIntentId,
  registerSalePaymentId,
}: GetRefundOptions): Promise<RefundResponse> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/refunds/${registerSalePaymentId}`,
    {
      method: 'POST',
      body: JSON.stringify({
        setup_intent_id: setupIntentId,
      }),
    }
  )

  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }
  return response.json()
}

// Server driven : Cancel the unref refund

export const cancelRefund = async ({
  registerId,
  setupIntentId,
  registerSalePaymentId,
}: GetRefundOptions): Promise<RefundResponse> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/refunds/${registerSalePaymentId}/cancel`,
    {
      method: 'POST',
      body: JSON.stringify({
        setup_intent_id: setupIntentId,
      }),
    }
  )

  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }
  return response.json()
}

// Server driven : Reprocess the unref refund

export const reprocessRefund = async ({
  registerId,
  setupIntentId,
  registerSalePaymentId,
}: ReprocessRefundOptions): Promise<RefundResponse> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/refunds/${registerSalePaymentId}/reprocess`,
    {
      method: 'POST',
      body: JSON.stringify({
        setup_intent_id: setupIntentId,
      }),
    }
  )

  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }
  return response.json()
}

export const shouldAllowUnreferencedRefunds = (
  unreferencedRefundFeatureFlag: boolean,
  internationalUnreferencedRefundFeatureFlag: boolean,
  currency: string
): boolean => {
  const lowerCaseCurrency = currency.toLowerCase()
  const usdCurrency = 'usd'

  const allowInternationalUnreferencedRefund =
    internationalUnreferencedRefundFeatureFlag &&
    lowerCaseCurrency !== usdCurrency

  const allowUSUnreferencedRefund =
    unreferencedRefundFeatureFlag && lowerCaseCurrency === usdCurrency

  return allowInternationalUnreferencedRefund || allowUSUnreferencedRefund
}

// Determines the an unref refund and return reason for a refund based on payment details of the original sale.
export const getUnrefRefundReason = (
  originalRegisterSalePaymentIds?: string[],
  originalSalePaidByOtherPT?: boolean
): UnrefRefundReason | undefined => {
  if (
    !originalRegisterSalePaymentIds ||
    originalRegisterSalePaymentIds.length < 1
  ) {
    // blind refund or non-lspay payment types
    return originalSalePaidByOtherPT
      ? UnrefRefundReason.DIFFERENT_PAYMENT_TYPES
      : UnrefRefundReason.BLIND_REFUND
  }

  // Refund multiple payments
  if (originalRegisterSalePaymentIds.length > 1) {
    return UnrefRefundReason.MULTIPLE_PAYMENTS
  }

  // No specific refund reason found for unreferenced refund
  return undefined
}

export const getUnreferencedMessage = (
  reason: UnrefRefundReason,
  retailerPaymentTypeName: string,
  t: TFunction
): string => {
  switch (reason) {
    case UnrefRefundReason.ZERO_DOLLAR_REFUNDABLE: {
      return t(
        'The original amount paid by {retailerPaymentTypeName} on this sale has been refunded, a card is required to be presented to process the remaining refund. You can continue or choose another refund type.',
        {
          _context: 'Lightspeed payments unreferenced refund message',
          retailerPaymentTypeName,
        }
      )
    }
    case UnrefRefundReason.MULTIPLE_PAYMENTS: {
      return t(
        'The original sale was paid in multiple payments, so a card is required to be presented to process the refund. You can continue or choose another payment type.',
        {
          _context: 'Lightspeed payments unreferenced refund message',
        }
      )
    }
    case UnrefRefundReason.BLIND_REFUND: {
      return t(
        'Because this refund isn’t linked to the original sale, a card is required for this refund to be processed. You can continue or choose another refund type',
        {
          _context: 'Lightspeed payments unreferenced refund message',
        }
      )
    }
    case UnrefRefundReason.DIFFERENT_PAYMENT_TYPES: {
      return t(
        'The original sale was paid using a different payment type so a card must be presented to process the refund. You can continue or choose another refund type.',
        {
          _context: 'Lightspeed payments unreferenced refund message',
        }
      )
    }
    default: {
      // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
      const _exhaustiveCheck: never = reason
      return _exhaustiveCheck
    }
  }
}

export const getRefundError = (errorCode: number | undefined): LSPAY_ERROR => {
  switch (errorCode) {
    case SWIPER_ERROR_CODES.LSPAY_UNREF_REFUND_NOT_ENABLED:
      return LSPAY_ERROR.UNREF_REFUND_NOT_ENABLED
    case SWIPER_ERROR_CODES.LSPAY_REFUND_IN_PROGRESS:
      return LSPAY_ERROR.REFUND_IN_PROGRESS
    case SWIPER_ERROR_CODES.LSPAY_REFUND_EXCEEDS_SALE_BALANCE:
      return LSPAY_ERROR.REFUND_EXCEEDS_SALE_BALANCE
    default:
      return LSPAY_ERROR.DEFAULT_REFUND
  }
}
